import {produce} from 'immer';
import reactUpdate, {Spec} from 'immutability-helper';
import {Dispatch} from 'react';
import isEqual from 'react-fast-compare';

import {buildDefaultConsensusConfiguration} from 'analyses/alignment/modals/SharedUtil';
import {track} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {
  AlignmentPreferences,
  AlignmentProgram,
  AlignmentProgramStrategy,
  BulkAlignmentCreationKind,
  ClustalOParameters,
  MafftParameters,
} from 'constants/AlignmentConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import preferences from 'Preferences';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {StepItemStatus} from 'ui-library/StepNav';
import {Optional} from 'util/Types';

import {
  performBulkAlignment,
  performRealignment,
  removeInput,
  setUseCurrentVersion,
  uploadAndParseFiles,
} from './createSequenceAlignmentsModalActions';

export enum StepKey {
  ChooseInput = 'ChooseInput',
  DefineParameters = 'DefineParameters',
}

export enum CreateSequenceAlignmentModalActionType {
  UPDATE_CURRENT_STEP = 'UPDATE_CURRENT_STEP',
  UPLOAD_FILES = 'UPLOAD_FILES',
  UPLOAD_FILE_SUCCESS = 'UPLOAD_FILE_SUCCESS',
  UPLOAD_FILE_FAILURE = 'UPLOAD_FILE_FAILURE',
  SELECT_PROGRAM = 'SELECT_PROGRAM',
  SELECT_PROGRAM_STRATEGY = 'SELECT_PROGRAM_STRATEGY',
  SELECT_ALIGNMENT_KIND = 'SELECT_ALIGNMENT_KIND',
  ADD_INPUT = 'ADD_INPUT',
  REMOVE_INPUT = 'REMOVE_INPUT',
  UPDATE_MAFFT_PARAMETERS = 'UPDATE_MAFFT_PARAMETERS',
  UPDATE_CLUSTALO_PARAMETERS = 'UPDATE_CLUSTALO_PARAMETERS',
  ADD_PAIRWISE_TEMPLATE = 'ADD_PAIRWISE_TEMPLATE',
  ADD_MULTISEQUENCE_TEMPLATE = 'ADD_MULTISEQUENCE_TEMPLATE',
  ADD_CONSENSUS_GROUP = 'ADD_CONSENSUS_GROUP',
  REMOVE_PAIRWISE_TEMPLATE = 'REMOVE_PAIRWISE_TEMPLATE',
  REMOVE_MULTISEQUENCE_TEMPLATE = 'REMOVE_MULTISEQUENCE_TEMPLATE',
  REMOVE_CONSENSUS_GROUP = 'REMOVE_CONSENSUS_GROUP',
  ADD_PAIRWISE_INPUT = 'ADD_PAIRWISE_INPUT',
  REMOVE_PAIRWISE_INPUT = 'REMOVE_PAIRWISE_INPUT',
  ADD_MULTISEQUENCE_INPUT = 'ADD_MULTISEQUENCE_INPUT',
  REMOVE_MULTISEQUENCE_INPUT = 'REMOVE_MULTISEQUENCE_INPUT',
  ADD_CONSENSUS_INPUT = 'ADD_CONSENSUS_INPUT',
  REMOVE_CONSENSUS_INPUT = 'REMOVE_CONSENSUS_INPUT',
  SET_CONSENSUS_GROUP_NAME = 'SET_CONSENSUS_GROUP_NAME ',
  SET_FOLDER = 'SET_FOLDER',
  PERFORM_ALIGNMENTS = 'PERFORM_ALIGNMENTS',
  SET_USE_CURRENT_VERSION = 'SET_USE_CURRENT_VERSION',
  MAYBE_AUTOPOPULATE_TEMPLATE_GROUP = 'MAYBE_AUTOPOPULATE_TEMPLATE_GROUP',
  SET_DISABLE_CIRCULAR_TEMPLATE_ROTATION = 'SET_DISABLE_CIRCULAR_TEMPLATE_ROTATION',
}

interface UpdateCurrentStepAction {
  type: CreateSequenceAlignmentModalActionType.UPDATE_CURRENT_STEP;
  value: StepKey;
}

interface UploadFilesAction {
  type: CreateSequenceAlignmentModalActionType.UPLOAD_FILES;
  files: Array<File>;
  dispatch: Dispatch<CreateSequenceAlignmentModalAction>;
}

interface UploadFileSuccessAction {
  type: CreateSequenceAlignmentModalActionType.UPLOAD_FILE_SUCCESS;
}

interface UploadFileFailureAction {
  type: CreateSequenceAlignmentModalActionType.UPLOAD_FILE_FAILURE;
}

interface SelectProgramAction {
  type: CreateSequenceAlignmentModalActionType.SELECT_PROGRAM;
  value: AlignmentProgram;
}

interface SelectProgramStrategyAction {
  type: CreateSequenceAlignmentModalActionType.SELECT_PROGRAM_STRATEGY;
  value: AlignmentProgramStrategy;
}

interface SelectAlignmentKindAction {
  type: CreateSequenceAlignmentModalActionType.SELECT_ALIGNMENT_KIND;
  value: BulkAlignmentCreationKind;
}

interface UpdateMafftParametersAction {
  type: CreateSequenceAlignmentModalActionType.UPDATE_MAFFT_PARAMETERS;
  parameters: Partial<MafftParameters>;
}

interface UpdateClustalOParametersAction {
  type: CreateSequenceAlignmentModalActionType.UPDATE_CLUSTALO_PARAMETERS;
  parameters: Partial<ClustalOParameters>;
}

interface AddInputAction {
  type: CreateSequenceAlignmentModalActionType.ADD_INPUT;
  input: AlignmentInput;
}

interface RemoveInputAction {
  type: CreateSequenceAlignmentModalActionType.REMOVE_INPUT;
  input: AlignmentInput;
}

interface AddPairwiseTemplateAction {
  type: CreateSequenceAlignmentModalActionType.ADD_PAIRWISE_TEMPLATE;
  template: AlignmentInput;
}

interface AddMultisequenceTemplateAction {
  type: CreateSequenceAlignmentModalActionType.ADD_MULTISEQUENCE_TEMPLATE;
  template: AlignmentInput;
}

interface AddConsensusGroupAction {
  type: CreateSequenceAlignmentModalActionType.ADD_CONSENSUS_GROUP;
}

interface RemovePairwiseTemplateAction {
  type: CreateSequenceAlignmentModalActionType.REMOVE_PAIRWISE_TEMPLATE;
  index: number;
}

interface RemoveMultisequenceTemplateAction {
  type: CreateSequenceAlignmentModalActionType.REMOVE_MULTISEQUENCE_TEMPLATE;
  index: number;
}

interface RemoveConsensusGroupAction {
  type: CreateSequenceAlignmentModalActionType.REMOVE_CONSENSUS_GROUP;
  index: number;
}

interface AddPairwiseInputAction {
  type: CreateSequenceAlignmentModalActionType.ADD_PAIRWISE_INPUT;
  index: number;
  input: AlignmentInput;
}

interface RemovePairwiseInputAction {
  type: CreateSequenceAlignmentModalActionType.REMOVE_PAIRWISE_INPUT;
  index: number;
  input: AlignmentInput;
}

interface AddMultisequenceInputAction {
  type: CreateSequenceAlignmentModalActionType.ADD_MULTISEQUENCE_INPUT;
  index: number;
  input: AlignmentInput;
}

interface RemoveMultisequenceInputAction {
  type: CreateSequenceAlignmentModalActionType.REMOVE_MULTISEQUENCE_INPUT;
  index: number;
  input: AlignmentInput;
}

interface AddConsensusInputAction {
  type: CreateSequenceAlignmentModalActionType.ADD_CONSENSUS_INPUT;
  index: number;
  input: AlignmentInput;
}

interface RemoveConsensusInputAction {
  type: CreateSequenceAlignmentModalActionType.REMOVE_CONSENSUS_INPUT;
  index: number;
  input: AlignmentInput;
}

interface SetConsensusGroupNameAction {
  type: CreateSequenceAlignmentModalActionType.SET_CONSENSUS_GROUP_NAME;
  index: number;
  name: string;
}

interface SetFolderAction {
  type: CreateSequenceAlignmentModalActionType.SET_FOLDER;
  folderId: string;
}

interface PerformAlignmentsAction {
  type: CreateSequenceAlignmentModalActionType.PERFORM_ALIGNMENTS;
}

interface SetUseCurrentVersionAction {
  type: CreateSequenceAlignmentModalActionType.SET_USE_CURRENT_VERSION;
  input: BioentityInput;
  value: boolean;
}

interface MaybeAutopopulateTemplateGroup {
  type: CreateSequenceAlignmentModalActionType.MAYBE_AUTOPOPULATE_TEMPLATE_GROUP;
}

interface SetDisableCircularTemplateRotationAction {
  type: CreateSequenceAlignmentModalActionType.SET_DISABLE_CIRCULAR_TEMPLATE_ROTATION;
  value: boolean;
}

export type CreateSequenceAlignmentModalAction =
  | UpdateCurrentStepAction
  | UploadFilesAction
  | UploadFileSuccessAction
  | UploadFileFailureAction
  | SelectProgramAction
  | SelectProgramStrategyAction
  | SelectAlignmentKindAction
  | AddInputAction
  | RemoveInputAction
  | UpdateMafftParametersAction
  | UpdateClustalOParametersAction
  | AddPairwiseTemplateAction
  | AddMultisequenceTemplateAction
  | AddConsensusGroupAction
  | RemovePairwiseTemplateAction
  | RemoveMultisequenceTemplateAction
  | RemoveConsensusGroupAction
  | AddPairwiseInputAction
  | RemovePairwiseInputAction
  | AddMultisequenceInputAction
  | RemoveMultisequenceInputAction
  | AddConsensusInputAction
  | RemoveConsensusInputAction
  | SetConsensusGroupNameAction
  | SetFolderAction
  | PerformAlignmentsAction
  | SetUseCurrentVersionAction
  | MaybeAutopopulateTemplateGroup
  | SetDisableCircularTemplateRotationAction;

// Keep in sync with backend AlignmentSequenceType enum in benchling/molbio/analyses/tasks.py
export enum InputType {
  FILE = 'FILE',
  TEXT = 'TEXT',
  BIOENTITY = 'BIOENTITY',
}

export function getIconForInput(input: AlignmentInput): BenchlingIconName {
  if (input.type === InputType.FILE) {
    return 'docs';
  } else if (input.nucleotideType === NucleotideType.DNA) {
    return FolderItemIcon.Sequence;
  } else {
    return FolderItemIcon.RNASequence;
  }
}

// Represents a single sequence parsed from a user-provided file. Zip and fasta files, for example, can
// contain many sequences so there may be multiple FileInput objects with the same `originalFileURL`.
export interface FileInput {
  type: InputType.FILE;
  name: string;
  originalFileURL: string;
  sequenceDictURL: string;
  nucleotideType: NucleotideType;
}

export interface TextInput {
  type: InputType.TEXT;
  name: string;
  residues: string;
  id: string;
  alignmentRowNumber?: number;
  nucleotideType: NucleotideType;
}

export interface BioentityInput {
  type: InputType.BIOENTITY;
  name: string;
  id: string;
  canBeTemplate: boolean;
  useCurrentVersion: boolean;
  nucleotideType: NucleotideType;
}

export type AlignmentInput = FileInput | TextInput | BioentityInput;

export interface TemplateSetup {
  template: AlignmentInput;
  items: Array<AlignmentInput>;
}

export interface ConsensusSetup {
  name: string;
  items: Array<AlignmentInput>;
  nucleotideType?: NucleotideType;
}

export type AlignmentSetup = TemplateSetup | ConsensusSetup;

export interface SequenceAnalysisInfo {
  sequenceAnalysisId: string;
  sequenceId: string;
  alignmentSetup: AlignmentSetup;
}

export interface RealignmentInfo {
  sequenceAnalysisInfo: SequenceAnalysisInfo;
  isTemplateSequenceEditable: boolean;
}

export interface CreateSequenceAlignmentModalState {
  statusByStep: {[key in StepKey]: StepItemStatus};
  currentStepKey: StepKey;
  numFilesUploading: number;
  inputs: Array<AlignmentInput>;
  folderId: Optional<string>;
  program: AlignmentProgram;
  mafftParameters: MafftParameters;
  clustalOParameters: ClustalOParameters;
  alignmentKind: BulkAlignmentCreationKind;
  pairwiseConfiguration: Array<TemplateSetup>;
  multisequenceConfiguration: Array<TemplateSetup>;
  consensusConfiguration: Array<ConsensusSetup>;
  defineParametersPaneAccessed: boolean;
  realignmentInfo?: RealignmentInfo;
  hasAutopopulatedTemplateGroup: {
    pairwiseConfiguration: boolean;
    multisequenceConfiguration: boolean;
  };
  disableCircularTemplateRotation: boolean;
}

export default function createSequenceAlignmentModalReducer(
  state: CreateSequenceAlignmentModalState,
  action: CreateSequenceAlignmentModalAction
): CreateSequenceAlignmentModalState {
  // Some actions are not triggerable during both flows, but it's easier to just define
  // this once and use it for all the analytics calls here.
  const metricName = state.realignmentInfo?.sequenceAnalysisInfo ? 'realignment' : 'alignment';
  switch (action.type) {
    case CreateSequenceAlignmentModalActionType.UPDATE_CURRENT_STEP: {
      track(metricName, 'changeStep', {step: action.value});
      return reactUpdate(state, {
        currentStepKey: {$set: action.value},
        statusByStep: {
          [StepKey.ChooseInput]: {
            $set: action.value === StepKey.DefineParameters ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
          },
        },
        // Set this as soon as we change panes at all, so we know to stop defaulting the consensus config
        defineParametersPaneAccessed: {$set: true},
      });
    }
    case CreateSequenceAlignmentModalActionType.UPLOAD_FILES: {
      uploadAndParseFiles(action.dispatch, action.files);
      return reactUpdate(state, {numFilesUploading: (count) => count + action.files.length});
    }
    case CreateSequenceAlignmentModalActionType.UPLOAD_FILE_SUCCESS: {
      return reactUpdate(state, {
        numFilesUploading: (count) => count - 1,
      });
    }
    case CreateSequenceAlignmentModalActionType.UPLOAD_FILE_FAILURE: {
      return reactUpdate(state, {numFilesUploading: (count) => count - 1});
    }
    case CreateSequenceAlignmentModalActionType.SELECT_PROGRAM: {
      return produce(state, (draft) => {
        draft.program = action.value;
      });
    }
    case CreateSequenceAlignmentModalActionType.SELECT_PROGRAM_STRATEGY: {
      if (action.value === AlignmentProgram.CLUSTALO) {
        track(metricName, 'changeAlignmentProgram', {program: AlignmentProgram.CLUSTALO});
        preferences.set(AlignmentPreferences.ALIGNMENT_PROGRAM, AlignmentProgram.CLUSTALO);
        return reactUpdate(state, {program: {$set: AlignmentProgram.CLUSTALO}});
      } else {
        track(metricName, 'changeAlignmentProgram', {program: AlignmentProgram.MAFFT});
        const updatedState = reactUpdate(state, {
          program: {$set: AlignmentProgram.MAFFT},
          mafftParameters: {$merge: {strategy: action.value}},
        });
        preferences.set(AlignmentPreferences.MAFFT_PARAMETERS, updatedState.mafftParameters);
        preferences.set(AlignmentPreferences.ALIGNMENT_PROGRAM, AlignmentProgram.MAFFT);
        return updatedState;
      }
    }
    case CreateSequenceAlignmentModalActionType.SELECT_ALIGNMENT_KIND: {
      track(metricName, 'changeAlignmentKind', {kind: action.value});
      assert(
        state.realignmentInfo == null ||
          [BulkAlignmentCreationKind.MULTISEQUENCE, BulkAlignmentCreationKind.CONSENSUS].includes(
            action.value
          ),
        'Pairwise alignments are not allowed for realignments.'
      );
      return reactUpdate(state, {alignmentKind: {$set: action.value}});
    }
    case CreateSequenceAlignmentModalActionType.ADD_INPUT: {
      track(metricName, 'addInput', {type: action.input.type});
      const updatesToApply: Spec<CreateSequenceAlignmentModalState> = {
        inputs: {
          $push: [action.input],
        },
      };
      // In addition to adding the input to the set of inputs (see below). We want to automatically
      // add inputs to the existing configurations if there is exactly 1 configuration.
      const addItemToFirstSetup = {
        0: {
          items: {
            $push: [action.input],
          },
        },
      };

      if (state.pairwiseConfiguration.length === 1) {
        updatesToApply.pairwiseConfiguration = addItemToFirstSetup;
      }
      if (state.multisequenceConfiguration.length === 1) {
        updatesToApply.multisequenceConfiguration = addItemToFirstSetup;
      }

      // Special case for consensus realignments: just add to the existing group. There should only be one;
      // we disable adding more groups for realignments.
      if (
        state.realignmentInfo != null &&
        state.alignmentKind === BulkAlignmentCreationKind.CONSENSUS &&
        state.consensusConfiguration.length === 1
      ) {
        updatesToApply.consensusConfiguration = addItemToFirstSetup;
      }

      // Special case for consensus alignments: if there's a mix of DNA and RNA inputs, then
      // we want to automatically make DNA and RNA configurations - as long as the user hasn't yet navigated
      // between panes, because we don't want to squash any changes they may have made.
      if (state.realignmentInfo == null && !state.defineParametersPaneAccessed) {
        updatesToApply.consensusConfiguration = {
          $set: buildDefaultConsensusConfiguration([...state.inputs, action.input]),
        };
      }

      return reactUpdate(state, updatesToApply);
    }
    case CreateSequenceAlignmentModalActionType.REMOVE_INPUT: {
      track(metricName, 'removeInput', {type: action.input.type});
      return removeInput(state, action.input);
    }
    case CreateSequenceAlignmentModalActionType.UPDATE_MAFFT_PARAMETERS: {
      track(metricName, 'changeAlignmentParameters');
      const updatedState = reactUpdate(state, {mafftParameters: {$merge: action.parameters}});
      preferences.set(AlignmentPreferences.MAFFT_PARAMETERS, updatedState.mafftParameters);
      return updatedState;
    }
    case CreateSequenceAlignmentModalActionType.UPDATE_CLUSTALO_PARAMETERS: {
      track(metricName, 'changeAlignmentParameters');
      const updatedState = reactUpdate(state, {clustalOParameters: {$merge: action.parameters}});
      preferences.set(AlignmentPreferences.CLUSTAL_O_PARAMETERS, updatedState.clustalOParameters);
      return updatedState;
    }
    case CreateSequenceAlignmentModalActionType.ADD_PAIRWISE_TEMPLATE: {
      track(metricName, 'addGroup', {kind: BulkAlignmentCreationKind.PAIRWISE});
      return reactUpdate(state, {
        pairwiseConfiguration: {
          $push: [{template: action.template, items: []}],
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.ADD_MULTISEQUENCE_TEMPLATE: {
      track(metricName, 'addGroup', {kind: BulkAlignmentCreationKind.MULTISEQUENCE});
      assert(
        state.realignmentInfo == null,
        'Realignments may only be performed for a single template at a time.'
      );
      return reactUpdate(state, {
        multisequenceConfiguration: {
          $push: [{template: action.template, items: []}],
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.ADD_CONSENSUS_GROUP: {
      track(metricName, 'addGroup', {kind: BulkAlignmentCreationKind.CONSENSUS});
      assert(
        state.realignmentInfo == null,
        'Realignments may only be performed for a single template at a time.'
      );
      return reactUpdate(state, {
        consensusConfiguration: {$push: [{name: 'Untitled Consensus', items: []}]},
      });
    }
    case CreateSequenceAlignmentModalActionType.REMOVE_PAIRWISE_TEMPLATE: {
      track(metricName, 'removeGroup', {kind: BulkAlignmentCreationKind.PAIRWISE});
      return reactUpdate(state, {
        pairwiseConfiguration: (setups) => setups.filter((_, i) => i !== action.index),
      });
    }
    case CreateSequenceAlignmentModalActionType.REMOVE_MULTISEQUENCE_TEMPLATE: {
      track(metricName, 'removeGroup', {kind: BulkAlignmentCreationKind.MULTISEQUENCE});
      assert(
        state.realignmentInfo == null,
        'Realignments may only be performed for a single template at a time.'
      );
      return reactUpdate(state, {
        multisequenceConfiguration: (setups) => setups.filter((_, i) => i !== action.index),
      });
    }
    case CreateSequenceAlignmentModalActionType.REMOVE_CONSENSUS_GROUP: {
      track(metricName, 'removeGroup', {kind: BulkAlignmentCreationKind.CONSENSUS});
      assert(
        state.realignmentInfo == null,
        'Realignments may only be performed for a single template at a time.'
      );
      return reactUpdate(state, {
        consensusConfiguration: (setups) => setups.filter((_, i) => i !== action.index),
      });
    }
    case CreateSequenceAlignmentModalActionType.ADD_PAIRWISE_INPUT: {
      return reactUpdate(state, {
        pairwiseConfiguration: {
          [action.index]: {
            items: {
              $push: [action.input],
            },
          },
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.REMOVE_PAIRWISE_INPUT: {
      return reactUpdate(state, {
        pairwiseConfiguration: {
          [action.index]: {
            items: {
              $apply: (inputs: Array<AlignmentInput>) =>
                inputs.filter((item) => !isEqual(item, action.input)),
            },
          },
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.ADD_MULTISEQUENCE_INPUT: {
      return reactUpdate(state, {
        multisequenceConfiguration: {
          [action.index]: {
            items: {
              $push: [action.input],
            },
          },
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.REMOVE_MULTISEQUENCE_INPUT: {
      return reactUpdate(state, {
        multisequenceConfiguration: {
          [action.index]: {
            items: {
              $apply: (inputs: Array<AlignmentInput>) =>
                inputs.filter((item) => !isEqual(item, action.input)),
            },
          },
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.ADD_CONSENSUS_INPUT: {
      return reactUpdate(state, {
        consensusConfiguration: {
          [action.index]: {
            items: {
              $push: [action.input],
            },
            nucleotideType: {
              $set: action.input.nucleotideType,
            },
          },
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.REMOVE_CONSENSUS_INPUT: {
      return reactUpdate(state, {
        consensusConfiguration: {
          [action.index]: {
            items: {
              $apply: (inputs: Array<AlignmentInput>) =>
                inputs.filter((item) => !isEqual(item, action.input)),
            },
          },
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.SET_CONSENSUS_GROUP_NAME: {
      return reactUpdate(state, {
        consensusConfiguration: {
          [action.index]: {
            name: {
              $set: action.name,
            },
          },
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.SET_FOLDER: {
      track(metricName, 'selectFolder');
      FolderDefaults.set(FolderDefaultsKey.INVENTORY, action.folderId);
      return reactUpdate(state, {folderId: {$set: action.folderId}});
    }
    case CreateSequenceAlignmentModalActionType.PERFORM_ALIGNMENTS: {
      track(metricName, 'clickPerformAlignment');
      if (state.realignmentInfo) {
        performRealignment(state);
      } else {
        performBulkAlignment(state);
      }
      return state;
    }
    case CreateSequenceAlignmentModalActionType.SET_USE_CURRENT_VERSION: {
      track(metricName, 'setUseCurrentVersion', {value: action.value});
      return setUseCurrentVersion(state, action.input, action.value);
    }
    case CreateSequenceAlignmentModalActionType.MAYBE_AUTOPOPULATE_TEMPLATE_GROUP: {
      // Special case for ease-of-use. The first time a user selects just 1 empty template group
      // they probably want to align all other inputs against that group.
      let key: 'multisequenceConfiguration' | 'pairwiseConfiguration';
      if (state.alignmentKind === BulkAlignmentCreationKind.MULTISEQUENCE) {
        key = 'multisequenceConfiguration';
      } else if (state.alignmentKind === BulkAlignmentCreationKind.PAIRWISE) {
        key = 'pairwiseConfiguration';
      } else {
        return state;
      }

      // Only prepopulate the group if this action hasn't been applied yet and there is exactly 1 empty group
      if (
        state.hasAutopopulatedTemplateGroup[key] ||
        state[key].length !== 1 ||
        state[key][0].items.length !== 0
      ) {
        return state;
      }

      return reactUpdate(state, {
        [key]: {
          0: {
            items: {
              $push: state.inputs.filter((input) => input !== state[key!][0].template),
            },
          },
        },
        hasAutopopulatedTemplateGroup: {
          [key]: {$set: true},
        },
      });
    }
    case CreateSequenceAlignmentModalActionType.SET_DISABLE_CIRCULAR_TEMPLATE_ROTATION: {
      preferences.set(AlignmentPreferences.DISABLE_CIRCULAR_TEMPLATE_ROTATION, action.value);
      return reactUpdate(state, {
        disableCircularTemplateRotation: {$set: action.value},
      });
    }
    default:
      return state;
  }
}
